import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { CheckCircle, RefreshCw, Search, X } from 'lucide-react';
import MainService from '../../services/MainService';
import { AlertBanner, Field, SectionCard, inputClass } from './accountingUi';
import { formatMoney, getFinancialYearId, parseApiList, parseKendoResult } from '../../utils/accountingHelpers';
import { buildKendoDateRange, groupGeneralLedgerByAccount, formatReportDate } from '../../utils/accountingReportHelpers';
import { orderDateToInputValue } from '../../utils/dateLocal';

const yearStartInput = () => {
  const now = new Date();
  return orderDateToInputValue(new Date(now.getFullYear(), 0, 1));
};

const GeneralLedgerReport = () => {
  const [dateFrom, setDateFrom] = useState(yearStartInput);
  const [dateTo, setDateTo] = useState(() => orderDateToInputValue(new Date()));
  const [docNo, setDocNo] = useState('');
  const [accountKeyword, setAccountKeyword] = useState('');
  const [accountOptions, setAccountOptions] = useState([]);
  const [selectedAccounts, setSelectedAccounts] = useState([]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (accountKeyword.trim().length < 2) {
      setAccountOptions([]);
      return undefined;
    }
    const timer = setTimeout(async () => {
      try {
        const response = await MainService.AccountListByKeyword({ Keyword: accountKeyword.trim() });
        setAccountOptions(parseApiList(response));
      } catch {
        setAccountOptions([]);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [accountKeyword]);

  const addAccount = (account) => {
    if (!account?.Id) return;
    setSelectedAccounts((prev) => (prev.some((a) => a.Id === account.Id) ? prev : [...prev, account]));
    setAccountKeyword('');
    setAccountOptions([]);
  };

  const removeAccount = (id) => {
    setSelectedAccounts((prev) => prev.filter((a) => a.Id !== id));
  };

  const loadRows = useCallback(async () => {
    const financialYearId = getFinancialYearId();
    if (!financialYearId) {
      setError('Financial year is not set. Log out and log in again.');
      setRows([]);
      return;
    }

    try {
      setLoading(true);
      setError('');
      const response = await MainService.GLInView({
        ...buildKendoDateRange(new Date(dateFrom), new Date(dateTo)),
        IsFilter: true,
        Accounts: selectedAccounts.map((a) => a.Id),
        DocNo: docNo.trim(),
        FinancialYearId: financialYearId,
        page: 1,
        pageSize: 10000,
        skip: 0,
        take: 10000,
      });
      const { rows: data } = parseKendoResult(response);
      setRows(data);
    } catch (err) {
      setError(err?.message || 'Failed to load general ledger');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo, docNo, selectedAccounts]);

  const accountGroups = useMemo(() => groupGeneralLedgerByAccount(rows), [rows]);

  const totals = accountGroups.reduce(
    (acc, group) => ({
      debit: acc.debit + group.totalDebit,
      credit: acc.credit + group.totalCredit,
    }),
    { debit: 0, credit: 0 }
  );
  const grandNetBalance = Math.round(Math.abs(totals.debit - totals.credit) * 100) / 100;

  return (
    <div className="space-y-4 p-4 md:p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-gray-900">General Ledger</h1>
          <p className="text-sm text-gray-500">View ledger entries by date range and account.</p>
        </div>
        <button
          type="button"
          onClick={loadRows}
          disabled={loading}
          className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
        >
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      <AlertBanner message={error} />

      <SectionCard title="Filters">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <Field label="Date from">
            <input type="date" className={inputClass} value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
          </Field>
          <Field label="Date to">
            <input type="date" className={inputClass} value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
          </Field>
          <Field label="Voucher no (optional)">
            <input
              className={inputClass}
              value={docNo}
              onChange={(e) => setDocNo(e.target.value)}
              placeholder="Filter by voucher number"
            />
          </Field>
        </div>

        <div className="mt-4">
          <Field label="Accounts (optional)">
            <div className="relative">
              <Search className="pointer-events-none absolute left-0 top-2.5 h-4 w-4 text-gray-400" />
              <input
                className={`${inputClass} pl-6`}
                value={accountKeyword}
                onChange={(e) => setAccountKeyword(e.target.value)}
                placeholder="Search accounts to filter..."
              />
            </div>
          </Field>
          {accountOptions.length > 0 ? (
            <div className="mt-1 max-h-40 overflow-y-auto rounded-lg border border-gray-200 bg-white shadow-sm">
              {accountOptions.map((account) => (
                <button
                  key={account.Id}
                  type="button"
                  onClick={() => addAccount(account)}
                  className="block w-full px-3 py-2 text-left text-sm hover:bg-gray-50"
                >
                  <span className="font-medium">{account.AccountCode}</span>
                  <span className="ml-2 text-gray-600">{account.Name}</span>
                </button>
              ))}
            </div>
          ) : null}
          {selectedAccounts.length > 0 ? (
            <div className="mt-2 flex flex-wrap gap-2">
              {selectedAccounts.map((account) => (
                <span
                  key={account.Id}
                  className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-3 py-1 text-xs font-medium text-primary"
                >
                  {account.AccountCode} · {account.Name}
                  <button type="button" onClick={() => removeAccount(account.Id)} className="hover:text-red-600">
                    <X className="h-3 w-3" />
                  </button>
                </span>
              ))}
            </div>
          ) : (
            <p className="mt-2 text-xs text-gray-500">Leave empty to include all accounts in the date range.</p>
          )}
        </div>

        <div className="mt-4">
          <button
            type="button"
            onClick={loadRows}
            disabled={loading}
            className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-green-700 disabled:opacity-60"
          >
            <CheckCircle className="h-4 w-4" />
            Load ledger
          </button>
        </div>
      </SectionCard>

      <SectionCard title="Ledger entries" bodyClassName="p-0">
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-xs font-semibold uppercase tracking-wide text-gray-600">
              <tr>
                <th className="px-3 py-3 text-left">Date</th>
                <th className="px-3 py-3 text-left">Voucher No</th>
                <th className="px-3 py-3 text-left">Type</th>
                <th className="px-3 py-3 text-right">Debit</th>
                <th className="px-3 py-3 text-right">Credit</th>
                <th className="px-3 py-3 text-right">Balance</th>
                <th className="px-3 py-3 text-left">Remarks</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                <tr>
                  <td colSpan={7} className="px-3 py-8 text-center text-gray-500">
                    Loading ledger entries...
                  </td>
                </tr>
              ) : accountGroups.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-3 py-8 text-center text-gray-500">
                    No ledger entries found. Adjust filters and load again.
                  </td>
                </tr>
              ) : (
                accountGroups.map((group) => (
                  <React.Fragment key={group.accountId}>
                    <tr className="bg-slate-100">
                      <td colSpan={7} className="px-3 py-2.5 text-sm font-semibold text-slate-800">
                        <span className="font-mono text-xs text-slate-600">{group.accountCode}</span>
                        <span className="mx-2 text-slate-400">·</span>
                        {group.accountName}
                      </td>
                    </tr>
                    {group.rows.map((row) => (
                      <tr key={row.Id} className="hover:bg-gray-50">
                        <td className="px-3 py-2">{formatReportDate(row.Date)}</td>
                        <td className="px-3 py-2 font-medium text-gray-900">{row.VoucherNumber || '-'}</td>
                        <td className="px-3 py-2">{row.Type || '-'}</td>
                        <td className="px-3 py-2 text-right tabular-nums">{formatMoney(row.Debit)}</td>
                        <td className="px-3 py-2 text-right tabular-nums">{formatMoney(row.Credit)}</td>
                        <td className="px-3 py-2 text-right tabular-nums font-medium">{formatMoney(row.RunningBalance)}</td>
                        <td className="px-3 py-2 text-gray-600">{row.Remarks || '-'}</td>
                      </tr>
                    ))}
                    <tr className="bg-gray-50 font-medium text-gray-800">
                      <td colSpan={3} className="px-3 py-2 text-right text-xs uppercase tracking-wide text-gray-500">
                        Account total
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums">{formatMoney(group.totalDebit)}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{formatMoney(group.totalCredit)}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{formatMoney(group.netBalance)}</td>
                      <td />
                    </tr>
                  </React.Fragment>
                ))
              )}
            </tbody>
            {accountGroups.length > 0 ? (
              <tfoot className="bg-gray-100 font-semibold text-gray-900">
                <tr>
                  <td colSpan={3} className="px-3 py-3 text-right text-xs uppercase tracking-wide">
                    Grand total
                  </td>
                  <td className="px-3 py-3 text-right tabular-nums">{formatMoney(totals.debit)}</td>
                  <td className="px-3 py-3 text-right tabular-nums">{formatMoney(totals.credit)}</td>
                  <td className="px-3 py-3 text-right tabular-nums">{formatMoney(grandNetBalance)}</td>
                  <td />
                </tr>
              </tfoot>
            ) : null}
          </table>
        </div>
      </SectionCard>
    </div>
  );
};

export default GeneralLedgerReport;
